// Guided setup requires a session and honors the server's enrollment gate.
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { checkSession, fetchMe, TWO_FACTOR_SETUP_PATH } from '@/lib/api';
import { reportPendingPurchaseConversion } from '@/lib/purchase-conversion';
import {
  isAuthenticationFailure,
  isTransientFailure,
  isUnreachableFailure,
  nonRetryableFailureKind,
  SESSION_RETRY_DELAYS_MS,
} from '@/lib/session-failure';

const UNREACHABLE_MESSAGE = 'Zoe cannot reach the server right now. Nothing has been lost, and you can try again in a moment.';
const SERVER_ERROR_MESSAGE = 'Zoe could not open this page just now. Nothing has been lost, and you can try again in a moment.';
const AUTHORIZATION_MESSAGE = 'Zoe will not open this page for the sign in you are using. Asking again returns the same answer, so signing in again is the way forward.';
const UNAVAILABLE_MESSAGE = 'Zoe could not open this page, and the fault is on our side rather than with anything you did. Asking again returns the same answer.';

export type SessionRecovery = 'retry' | 'signin' | 'home';
export interface SessionCheckError {
  message: string;
  recovery: SessionRecovery;
}

export function sessionCheckError(error: unknown): SessionCheckError {
  const kind = nonRetryableFailureKind(error);
  if (kind === 'authorization') return { message: AUTHORIZATION_MESSAGE, recovery: 'signin' };
  if (kind === 'unavailable') return { message: UNAVAILABLE_MESSAGE, recovery: 'home' };
  return {
    message: isUnreachableFailure(error) ? UNREACHABLE_MESSAGE : SERVER_ERROR_MESSAGE,
    recovery: 'retry',
  };
}

export const CHECKING_STYLE = {
  minHeight: 'calc(100vh - var(--zoe-debug-bar-height, 0px))',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--paper, #FAF8F3)',
} as const;

/** The session check's error state: what went wrong, and the one action that can get past it. */
export function SessionCheckErrorPanel({
  error,
  onRetry,
  onSignIn,
  onHome,
}: {
  error: SessionCheckError;
  onRetry: () => void;
  onSignIn: () => void;
  onHome: () => void;
}) {
  return (
    <div data-v2 className="mul" style={CHECKING_STYLE}>
      <div role="alert" style={{ display: 'grid', gap: 12, justifyItems: 'center', maxWidth: 420, padding: 24, textAlign: 'center' }}>
        <div style={{ font: "10px/1.6 'JetBrains Mono', monospace", letterSpacing: '.08em', color: 'var(--dim, #656C79)' }}>
          {error.message}
        </div>
        {error.recovery === 'retry' && (
          <button type="button" className="v-btn" onClick={onRetry}>
            Try again
          </button>
        )}
        {error.recovery === 'signin' && (
          <button type="button" className="v-btn" onClick={onSignIn}>
            Sign in again
          </button>
        )}
        {error.recovery === 'home' && (
          <button type="button" className="v-btn" onClick={onHome}>
            Go to the home page
          </button>
        )}
      </div>
    </div>
  );
}

export function RequireAuth() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [requiresEnrollment, setRequiresEnrollment] = useState(false);
  const [checkError, setCheckError] = useState<SessionCheckError | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const run = (attempt: number) => {
      checkSession()
        .then(async (result) => {
          const me = result.authed ? await fetchMe() : null;
          if (cancelled) return;
          setCheckError(null);
          setRequiresEnrollment(me?.security?.two_factor_enrollment_required === true);
          setAuthed(result.authed);
        })
        .catch((error: unknown) => {
          if (cancelled) return;

          // The only answer that means "sign in again".
          if (isAuthenticationFailure(error)) {
            setAuthed(false);
            return;
          }

          if (isTransientFailure(error) && attempt < SESSION_RETRY_DELAYS_MS.length) {
            retryTimer = setTimeout(() => run(attempt + 1), SESSION_RETRY_DELAYS_MS[attempt]);
            return;
          }

          setCheckError(sessionCheckError(error));
        });
    };

    setCheckError(null);
    run(0);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, [retryNonce]);

  const errored = checkError !== null && checkError.recovery === 'retry';
  useEffect(() => {
    if (!errored) return;
    const onOnline = () => setRetryNonce((n) => n + 1);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [errored]);

  useEffect(() => {
    if (authed !== true) return;
    void reportPendingPurchaseConversion();
  }, [authed]);

  const retrySessionCheck = useCallback(() => setRetryNonce((n) => n + 1), []);
  const goToSignIn = useCallback(
    () => navigate(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`),
    [navigate, location.pathname, location.search],
  );
  const goHome = useCallback(() => navigate('/'), [navigate]);

  if (authed === null && checkError !== null) {
    return (
      <SessionCheckErrorPanel
        error={checkError}
        onRetry={retrySessionCheck}
        onSignIn={goToSignIn}
        onHome={goHome}
      />
    );
  }

  if (authed === null) {
    return (
      <div className="mul" style={CHECKING_STYLE}>
        <div style={{ font: "10px/1.4 'JetBrains Mono', monospace", letterSpacing: '.14em', color: 'var(--dim, #656C79)' }} role="status">
          CHECKING YOUR SESSION…
        </div>
      </div>
    );
  }

  if (!authed) {
    return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (requiresEnrollment) return <Navigate to={`${TWO_FACTOR_SETUP_PATH}?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  return <Outlet />;
}
